import { supabase, isSupabaseConfigured } from './supabase';

export const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
export const MAX_AVATAR_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

/**
 * Validates a profile picture file for allowed MIME type, extension, and file size limit.
 * @param {File} file - The file object from input.
 * @returns {{ valid: boolean, error: string | null, extension: string | null }}
 */
export function validateAvatarFile(file) {
  if (!file) {
    return { valid: false, error: 'No image file selected.', extension: null };
  }

  // 1. Validate File Size (Max 5 MB)
  if (file.size > MAX_AVATAR_SIZE_BYTES) {
    return {
      valid: false,
      error: 'Profile picture must be 5 MB or smaller.',
      extension: null,
    };
  }

  // 2. Validate MIME Type
  const mimeType = (file.type || '').toLowerCase();
  if (!ALLOWED_MIME_TYPES.includes(mimeType)) {
    return {
      valid: false,
      error: 'Please choose a JPG, PNG, or WEBP image.',
      extension: null,
    };
  }

  // 3. Validate Filename Extension
  const filename = file.name || '';
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return {
      valid: false,
      error: 'Please choose a JPG, PNG, or WEBP image.',
      extension: null,
    };
  }

  const ext = filename.slice(lastDotIndex + 1).toLowerCase();
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return {
      valid: false,
      error: 'Please choose a JPG, PNG, or WEBP image.',
      extension: null,
    };
  }

  // Map jpeg to jpg for clean deterministic filenames
  const normalizedExt = ext === 'jpeg' ? 'jpg' : ext;

  return {
    valid: true,
    error: null,
    extension: normalizedExt,
  };
}

/**
 * Uploads an avatar image to Supabase Storage and updates the profiles table atomically.
 * @param {File} file - The image file to upload.
 * @returns {Promise<{ success: boolean, avatarUrl: string }>}
 */
export async function uploadAvatarImage(file) {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured.');
  }

  // 1. Verify Authenticated User
  const { data: authData, error: authError } = await supabase.auth.getSession();
  const currentUser = authData?.session?.user;
  if (authError || !currentUser?.id) {
    throw new Error('Please log in to change your profile picture.');
  }

  const userId = currentUser.id;

  // 2. Validate File
  const validation = validateAvatarFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  const filePath = `${userId}/avatar.${validation.extension}`;

  // 3. Upload to Storage Bucket 'profile-avatars'
  try {
    const { error: uploadError } = await supabase.storage
      .from('profile-avatars')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) {
      console.error('[avatarService] Storage upload error:', uploadError);
      throw new Error('Profile picture upload failed. Please try again.');
    }
  } catch (err) {
    console.error('[avatarService] Upload exception:', err);
    throw new Error(err.message || 'Profile picture upload failed. Please try again.');
  }

  // 4. Obtain Public URL
  const { data: urlData } = supabase.storage
    .from('profile-avatars')
    .getPublicUrl(filePath);

  const cleanPublicUrl = urlData?.publicUrl;
  if (!cleanPublicUrl) {
    throw new Error('Profile picture upload failed. Please try again.');
  }

  // 5. Update Database profiles.avatar_url
  try {
    const { error: dbError } = await supabase
      .from('profiles')
      .update({
        avatar_url: cleanPublicUrl,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (dbError) {
      console.error('[avatarService] Database update error:', dbError);
      throw new Error('Your image was uploaded, but your profile could not be updated. Please try again.');
    }

    // Also sync to artist_profiles if existing
    const { error: artistError } = await supabase
      .from('artist_profiles')
      .update({
        avatar_url: cleanPublicUrl,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId);

    if (artistError) {
      console.warn('[avatarService] Artist profile sync notice:', artistError.message);
    }

    return {
      success: true,
      avatarUrl: cleanPublicUrl,
    };
  } catch (err) {
    console.error('[avatarService] Database update exception:', err);
    throw new Error(err.message || 'Your image was uploaded, but your profile could not be updated. Please try again.');
  }
}

/**
 * Removes an existing avatar image from storage and resets profiles.avatar_url to null.
 * @returns {Promise<{ success: boolean }>}
 */
export async function removeAvatarImage() {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured.');
  }

  // 1. Verify Authenticated User
  const { data: authData, error: authError } = await supabase.auth.getSession();
  const currentUser = authData?.session?.user;
  if (authError || !currentUser?.id) {
    throw new Error('Please log in to change your profile picture.');
  }

  const userId = currentUser.id;

  // 2. Remove files from Storage
  try {
    const commonPaths = [
      `${userId}/avatar.jpg`,
      `${userId}/avatar.jpeg`,
      `${userId}/avatar.png`,
      `${userId}/avatar.webp`,
    ];
    await supabase.storage.from('profile-avatars').remove(commonPaths);
  } catch (err) {
    console.warn('[avatarService] Storage removal notice:', err);
  }

  // 3. Update Database profiles.avatar_url = null
  try {
    const { error: dbError } = await supabase
      .from('profiles')
      .update({
        avatar_url: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (dbError) {
      console.error('[avatarService] DB reset error:', dbError);
      throw new Error('Unable to remove your profile picture. Please try again.');
    }

    // Also sync to artist_profiles
    await supabase
      .from('artist_profiles')
      .update({
        avatar_url: null,
        updated_at: new Date().toISOString(),
      })
      .eq('user_id', userId);

    return { success: true };
  } catch (err) {
    console.error('[avatarService] Reset exception:', err);
    throw new Error(err.message || 'Unable to remove your profile picture. Please try again.');
  }
}
